<script>
    import { onMount, onDestroy } from "svelte";
    import { Chart, registerables, CategoryScale, LinearScale, BarController, BarElement, Tooltip, Legend } from "chart.js";
    import annotationPlugin from "chartjs-plugin-annotation";
    import { theme } from "$stores/themeStore";
    import { get } from "svelte/store";
    import { selectedStudy, selectedParticipant } from "$stores/selectionStore";
    import { studyProperties, fetchStudyProperties, isFeatureEnabled } from "$stores/configStore";

    Chart.register(
        ...registerables,
        CategoryScale,
        LinearScale,
        BarController,
        BarElement,
        Tooltip,
        Legend,
        annotationPlugin
    );

    export let data = [];
    export let sensorStartDay = null;
    export let sensorEndDay = null;
    export let linePercentage = 90; // Will be overridden by study properties
    export let thresholdBars = 14; // Will be overridden by study properties

    let canvas;
    let chart;
    let chartBorderColor = "rgba(8, 89, 133, 1)";
    let chartBackgroundColor = "rgba(8, 89, 133, 0.2)";

    // Expected readings per day based on study interval
    $: expectedPerDay = Math.round(1440 / (($studyProperties?.glucoseParameters?.cgmIntervalMinutes ?? 5)));

    function getAnnotationsConfig(isLightMode) {
        const maxValue = expectedPerDay;
        const yPosition = (linePercentage / 100) * maxValue;

        const baseConfig = {
            line1: {
                type: "line",
                yMin: yPosition,
                yMax: yPosition,
                borderColor: isLightMode ? "rgba(120, 20, 80, 1.0)" : "rgba(110, 210, 170, 0.8)",
                borderWidth: 0.5,
                label: {
                    display: true,
                    content: `${linePercentage}%`,
                    position: "end",
                    backgroundColor: isLightMode ? "rgba(255,255,255,0.8)" : "rgba(50,50,50,0.8)",
                    color: isLightMode ? "rgba(120, 20, 80, 1.0)" : "rgba(110, 210, 170, 0.8)",
                    font: { size: 10 }
                }
            }
        };

        if (sensorStartDay !== null) {
            baseConfig.sensorStart = {
                type: 'line',
                xMin: sensorStartDay,
                xMax: sensorStartDay,
                borderColor: isLightMode ? 'rgba(255, 0, 0, 0.5)' : 'rgba(255, 99, 132, 0.5)',
                borderWidth: 2,
                borderDash: [6, 6],
                label: {
                    display: true,
                    content: 'Sensor Start',
                    position: 'top',
                    color: isLightMode ? 'black' : 'white'
                }
            };
        }

        if (sensorEndDay !== null) {
            baseConfig.sensorEnd = {
                type: 'line',
                xMin: sensorEndDay,
                xMax: sensorEndDay,
                borderColor: isLightMode ? 'rgba(255, 0, 0, 0.5)' : 'rgba(255, 99, 132, 0.5)',
                borderWidth: 2,
                borderDash: [6, 6],
                label: {
                    display: true,
                    content: 'Sensor End',
                    position: 'top',
                    color: isLightMode ? 'black' : 'white'
                }
            };
        }

        return baseConfig;
    }

    const lightModeOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                stacked: true,
                title: {
                    display: false,
                    text: "Serial Number",
                    color: "black",
                },
                ticks: {
                    color: "black",
                    maxRotation: 90,
                    minRotation: 90,
                    callback: function(value, index) {
                        // Debug: log what we're receiving
                        // console.log('Chart callback - value:', value, 'index:', index, 'data[index]:', data[index]);
                        
                        if (data && data[index] && data[index].day) {
                            // Parse the date string directly to avoid timezone issues
                            const [year, month, day] = data[index].day.split('-');
                            const result = `${parseInt(month)}/${parseInt(day)}`;
                            // console.log('Chart callback - returning:', result);
                            return result;
                        }
                        return value;
                    }
                },
            },
            y: {
                stacked: true,
                beginAtZero: true,
                title: {
                    display: true,
                    text: "Count",
                    color: "black",
                },
                position: "right",
                ticks: {
                    color: "black",
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            canvas: {
                responsive: true,
                maintainAspectRatio: false,
                resizeDelay: 1000,
            },
            tooltip: {
                callbacks: {
                    title: (tooltipItems) => {
                        const dataIndex = tooltipItems[0].dataIndex;
                        return [
                            `${data[dataIndex].day}`
                        ];
                    },
                    label: (context) => {
                        const dataIndex = context.dataIndex;
                        return [
                            `Count: ${context.raw}`
                        ];
                    }
                }
            },
        },
    };

    const darkModeOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                stacked: true,
                title: {
                    display: false,
                    text: "Serial Number",
                    color: "white",
                },
                ticks: {
                    color: "white",
                    maxRotation: 90,
                    minRotation: 90,
                    callback: function(value, index) {
                        // Debug: log what we're receiving
                        // console.log('Chart callback - value:', value, 'index:', index, 'data[index]:', data[index]);
                        
                        if (data && data[index] && data[index].day) {
                            // Parse the date string directly to avoid timezone issues
                            const [year, month, day] = data[index].day.split('-');
                            const result = `${parseInt(month)}/${parseInt(day)}`;
                            // console.log('Chart callback - returning:', result);
                            return result;
                        }
                        return value;
                    }
                },
            },
            y: {
                stacked: true,
                beginAtZero: true,
                title: {
                    display: true,
                    text: "Count",
                    color: "white",
                },
                position: "right",
                ticks: {
                    color: "white",
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            canvas: {
                responsive: true,
                maintainAspectRatio: false,
                resizeDelay: 1000,
            },
            tooltip: {
                callbacks: {
                    title: (tooltipItems) => {
                        const dataIndex = tooltipItems[0].dataIndex;
                        return [
                            `${data[dataIndex].day}`
                        ];
                    },
                    label: (context) => {
                        const dataIndex = context.dataIndex;
                        return [
                            `Count: ${context.raw}`
                        ];
                    }
                }
            },
        },
    };

    function destroyChart() {
        if (chart) {
            chart.destroy();
            chart = null;
        }
        if (canvas) {
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    }

    function getBarColors() {
        let backgrounds = [];
        let borders = [];
        
        // Calculate the threshold value based on percentage
        const maxValue = expectedPerDay;
        const threshold = (linePercentage / 100) * maxValue;
        
        // Check if randomization feature is enabled and participant has randomization date
        const isRandomizationEnabled = isFeatureEnabled('randomization.enabled');
        const randomizationDate = $selectedParticipant?.randomizationDate;
        
        // Color pairs for each theme
        const lightColors = {
            below: { bg: "rgba(140, 140, 140, 0.4)", border: "rgba(140, 140, 140, 0.75)" },
            above: { bg: "rgba(34, 197, 94, 0.4)", border: "rgba(34, 197, 94, 0.75)" },
            randomizationBelow: { bg: "rgba(140, 140, 140, 0.4)", border: "rgba(0, 0, 0, 0.75)" }, // Black border for randomization below threshold
            randomizationAbove: { bg: "rgba(34, 197, 94, 0.4)", border: "rgba(0, 0, 0, 0.75)" } // Black border for randomization above threshold
        };
        
        const darkColors = {
            below: { bg: "rgba(120, 120, 120, 0.4)", border: "rgba(120, 120, 120, 0.75)" },
            above: { bg: "rgba(34, 197, 94, 0.4)", border: "rgba(34, 197, 94, 0.75)" },
            randomizationBelow: { bg: "rgba(120, 120, 120, 0.4)", border: "rgba(255, 255, 255, 0.75)" }, // White border for randomization below threshold
            randomizationAbove: { bg: "rgba(34, 197, 94, 0.4)", border: "rgba(255, 255, 255, 0.75)" } // White border for randomization above threshold
        };

        const colors = get(theme) === "dark" ? darkColors : lightColors;
        
        // Assign colors based on value compared to threshold, but only for first thresholdBars
        data.forEach((item, index) => {
            // const isAboveThreshold = index < thresholdBars ? item.count >= threshold : false;
            const isAboveThreshold = item.count >= threshold;

            // Check if this is the randomization date
            let isRandomizationDay = false;
            if (isRandomizationEnabled && randomizationDate && item.day) {
                // Parse item date directly
                const [itemYear, itemMonth, itemDay] = item.day.split('-');
                
                // Parse randomization date - handle both ISO string and date string formats
                let randYear, randMonth, randDay;
                if (typeof randomizationDate === 'string') {
                    if (randomizationDate.includes('T')) {
                        // ISO string format: "2025-07-21T00:00:00.000Z"
                        [randYear, randMonth, randDay] = randomizationDate.split('T')[0].split('-');
                    } else {
                        // Date string format: "2025-07-21"
                        [randYear, randMonth, randDay] = randomizationDate.split('-');
                    }
                } else if (randomizationDate instanceof Date) {
                    // Date object
                    randYear = randomizationDate.getFullYear().toString();
                    randMonth = (randomizationDate.getMonth() + 1).toString().padStart(2, '0');
                    randDay = randomizationDate.getDate().toString().padStart(2, '0');
                }
                
                // Compare year, month, and day directly
                isRandomizationDay = itemYear === randYear && itemMonth === randMonth && itemDay === randDay;
                
            }
            
            if (isRandomizationDay) {
                if (isAboveThreshold) {
                    backgrounds.push(colors.randomizationAbove.bg);
                    borders.push(colors.randomizationAbove.border);
                } else {
                    backgrounds.push(colors.randomizationBelow.bg);
                    borders.push(colors.randomizationBelow.border);
                }
            } else {
                backgrounds.push(isAboveThreshold ? colors.above.bg : colors.below.bg);
                borders.push(isAboveThreshold ? colors.above.border : colors.below.border);
            }
        });
        
        return { backgrounds, borders };
    }

    function createChart() {
        if (!canvas) {
            console.warn('Canvas element not found');
            return;
        }

        destroyChart();

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            console.error('Unable to get 2D context from canvas');
            return;
        }

        const currentTheme = get(theme);
        const options = currentTheme === "dark" ? darkModeOptions : lightModeOptions;
        // Update annotation config with latest linePercentage
        options.plugins.annotation = {
            annotations: getAnnotationsConfig(currentTheme === "dark" ? false : true)
        };

        const { backgrounds, borders } = getBarColors();

        try {
            chart = new Chart(ctx, {
                type: "bar",
                data: {
                    labels: data.map((item) => item.day),
                    datasets: [{
                        label: "Count",
                        data: data.map((item) => item.count),
                        backgroundColor: backgrounds,
                        borderColor: borders,
                        borderWidth: 1,
                    }],
                },
                options: options,
            });
        } catch (error) {
            console.error('Error creating chart:', error);
        }
    }

    function handleThemeChange() {
        chartBorderColor = get(theme) === "dark" ? "rgba(13, 148, 136, 1)" : "rgba(8, 89, 133, 1)";
        chartBackgroundColor = get(theme) === "dark" ? "rgba(13, 148, 136, 0.2)" : "rgba(8, 89, 133, 0.2)";
        if (canvas) {
            createChart();
        }
    }

    onMount(async () => {
        // Fetch study properties to get CGM compliance settings
        if ($selectedStudy?._id) {
            await fetchStudyProperties($selectedStudy._id);
        }
    });

    onDestroy(() => {
        destroyChart();
    });

    // Update linePercentage and thresholdBars from study properties
    $: if ($studyProperties?.glucoseParameters?.cgmCompliance) {
        linePercentage = $studyProperties.glucoseParameters.cgmCompliance.complianceThreshold;
        thresholdBars = $studyProperties.glucoseParameters.cgmCompliance.complianceFocus;
        // console.log('CGM Chart: complianceThreshold from store:', $studyProperties.glucoseParameters.cgmCompliance.complianceThreshold, 'linePercentage:', linePercentage);
    }

    $: if ((data && data.length > 0 && canvas) || linePercentage || thresholdBars) {
        // console.log('CGM Chart: Data received:', data?.length, 'records');
        // console.log('CGM Chart: First few records:', data?.slice(0, 3));
        setTimeout(() => {
            createChart();
        }, 0);
    }

    $: if ($theme && canvas) {
        handleThemeChange();
    }
</script>

<div class="w-full h-64 flex justify-center">
    {#if !data || data.length === 0}
        <div class="w-full h-full flex items-center justify-center">
            <div class="text-gray-500">No CGM data available</div>
        </div>
    {:else}
        <div class="w-full h-full">
            <canvas bind:this={canvas}></canvas>
        </div>
    {/if}
</div>